import React from 'react';
import { ActivityFilters, ActivityTypeFilter } from '@/components/homepage/ActivityTypeFilter';
import { AnimalTypeFilter } from '@/components/homepage/AnimalTypeFilter';
import { AdventureTypeFilter } from '@/components/homepage/AdventureTypeFilter';
import { Button } from '@/components/ui/button';
import { Calendar, Users, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SEASONS } from '@/constants/seasons';
import { useTranslation } from 'react-i18next';

interface MobileFilterContentProps {
  activityFilters: ActivityFilters;
  selectedAnimalTypes: string[];
  selectedAdventureTypes: string[];
  selectedSeason: string | null;
  groupSize: [number, number];
  onActivityFilterChange: (filters: ActivityFilters) => void;
  onAnimalTypeChange: (selected: string[]) => void;
  onAdventureTypeChange: (selected: string[]) => void;
  onSeasonChange: (season: string | null) => void;
  onGroupSizeChange: (size: [number, number]) => void;
  onClearFilters: () => void;
  showClearFilters: boolean; // Keep for future use, just not rendering the button
  /** Hide Season and Group size (not applied to results yet). Default true. */
  hidePlaceboFilters?: boolean;
}

export function MobileFilterContent({
  activityFilters,
  selectedAnimalTypes,
  selectedAdventureTypes,
  selectedSeason,
  groupSize,
  onActivityFilterChange,
  onAnimalTypeChange,
  onAdventureTypeChange,
  onSeasonChange,
  onGroupSizeChange,
  onClearFilters,
  showClearFilters,
  hidePlaceboFilters = true
}: MobileFilterContentProps) {
  const { t } = useTranslation('common');

  // Season filter logic
  const handleSeasonChange = (season: string | null) => {
    onSeasonChange(season);
  };

  // Group size logic
  const handleGroupSizeChange = (newSize: [number, number]) => {
    onGroupSizeChange(newSize);
  };

  const incrementGroupSize = (type: 'min' | 'max') => {
    if (type === 'min' && groupSize[0] < groupSize[1]) {
      handleGroupSizeChange([groupSize[0] + 1, groupSize[1]]);
    } else if (type === 'max' && groupSize[1] < 20) {
      handleGroupSizeChange([groupSize[0], groupSize[1] + 1]);
    }
  };

  const decrementGroupSize = (type: 'min' | 'max') => {
    if (type === 'min' && groupSize[0] > 1) {
      handleGroupSizeChange([groupSize[0] - 1, groupSize[1]]);
    } else if (type === 'max' && groupSize[1] > groupSize[0]) {
      handleGroupSizeChange([groupSize[0], groupSize[1] - 1]);
    }
  };

  return (
    <div className="space-y-5"> {/* Reduced spacing */}
      {/* Activity Filters */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t('filters.experienceTypes')}</h3>
        <ActivityTypeFilter
          filters={activityFilters}
          onChange={onActivityFilterChange}
        />
      </div>

      {/* Filters in Two Columns for Better Space Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Animal Type Filter */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('filters.animalTypes')}</h3>
          <div className="max-h-48 overflow-y-auto"> {/* Constrained height */}
            <AnimalTypeFilter
              selected={selectedAnimalTypes}
              onChange={onAnimalTypeChange}
            />
          </div>
        </div>

        {/* Adventure Type Filter */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('filters.adventureTypes')}</h3>
          <div className="max-h-48 overflow-y-auto"> {/* Constrained height */}
            <AdventureTypeFilter
              selected={selectedAdventureTypes}
              onChange={onAdventureTypeChange}
            />
          </div>
        </div>
      </div>

      {!hidePlaceboFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Season Filter - not applied to results yet */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-900">{t('filters.bestSeason')}</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SEASONS.map((season) => (
                <Button
                  key={season.id}
                  variant={selectedSeason === season.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleSeasonChange(selectedSeason === season.id ? null : season.id)}
                  className={cn(
                    "justify-start text-xs min-h-[44px]",
                    selectedSeason === season.id
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <span className="mr-1.5">{season.emoji}</span>
                  <span className="truncate">{season.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Group Size Filter - not applied to results yet */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-900">{t('filters.groupSize')}</h3>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">{t('filters.min')}</span>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => decrementGroupSize('min')}
                    disabled={groupSize[0] <= 1}
                    className="h-7 w-7 p-0 text-xs"
                  >
                    -
                  </Button>
                  <span className="w-6 text-center text-xs font-medium">{groupSize[0]}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => incrementGroupSize('min')}
                    disabled={groupSize[0] >= groupSize[1]}
                    className="h-7 w-7 p-0 text-xs"
                  >
                    +
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">{t('filters.max')}</span>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => decrementGroupSize('max')}
                    disabled={groupSize[1] <= groupSize[0]}
                    className="h-7 w-7 p-0 text-xs"
                  >
                    -
                  </Button>
                  <span className="w-6 text-center text-xs font-medium">{groupSize[1]}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => incrementGroupSize('max')}
                    disabled={groupSize[1] >= 20}
                    className="h-7 w-7 p-0 text-xs"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
} 